import { DatePicker, DateTimePicker } from '@mui/x-date-pickers';
import { Dayjs } from 'dayjs';
import { ComponentType, useRef, useState } from 'react';
import { CustomAttributeValue } from 'src/graphql/entities/custom-attributes/custom-attributes.types';
import { CustomAttributeEditProps } from 'src/graphql/entities/custom-attributes/field-view-registry/types';
import { getKindLikeValueSafely } from 'src/graphql/entities/custom-attributes/field-view-registry/utils';
import { toDayjs, toIso } from 'src/utils/date';

type DateKind = 'date' | 'date_time';

interface PickerProps {
  value: Dayjs | null;
  open: boolean;
  onClose: () => void;
  onChange: (next: Dayjs | null) => void;
  onAccept: (next: Dayjs | null) => void;
  disabled: boolean;
  slotProps: typeof SLOT_PROPS;
  timezone?: string;
}

const SLOT_PROPS = {
  textField: { size: 'small', fullWidth: true, autoFocus: true },
  field: { clearable: true },
} as const;

const renderDateEditor = (kind: DateKind, Picker: ComponentType<PickerProps>) => {
  const Editor = ({
    value,
    onChange,
    onSave,
    onCancel,
    disabled,
    timezone,
  }: CustomAttributeEditProps) => {
    const [open, setOpen] = useState(true);
    const currentIso = getKindLikeValueSafely(value, kind);
    const initialIsoRef = useRef(currentIso);
    const settledRef = useRef(false);

    return (
      <Picker
        value={toDayjs(currentIso)}
        timezone={timezone}
        open={open}
        onClose={() => {
          setOpen(false);
          if (settledRef.current) return;
          settledRef.current = true;
          onCancel();
        }}
        onChange={(next) => onChange({ kind, value: toIso(next) })}
        onAccept={(next) => {
          const iso = toIso(next);
          const value: CustomAttributeValue = { kind, value: iso };
          onChange(value);
          setOpen(false);
          if (settledRef.current) return;
          settledRef.current = true;
          if (iso !== initialIsoRef.current) onSave(value);
          else onCancel();
        }}
        disabled={disabled}
        slotProps={SLOT_PROPS}
      />
    );
  };
  Editor.displayName = `${kind === 'date' ? 'Date' : 'DateTime'}Editor`;
  return Editor;
};

export const DateEditor = renderDateEditor('date', DatePicker as ComponentType<PickerProps>);

export const DateTimeEditor = renderDateEditor(
  'date_time',
  DateTimePicker as ComponentType<PickerProps>,
);
